import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './PreparedStickerCard.css';
import { getColorFilter, COLOR_NAMES } from '../data/colors';

const PreparedStickerCard = ({ 
  svgPath, 
  title, 
  size = 'MEDIUM', 
  colorIndex = 0, // Use index 0-20 instead of string names
  width,
  height,
  className = '',
  style = {},
  onStickerClick = null,
  id = null, // <-- add id prop
  isFlipped = false, // <-- add isFlipped prop for pack handling
  isLoading = false, // <-- add loading state prop
  // New props for interaction buttons
  showInteractionButtons = false,
  onNewSticker = null,
  portId = null,
  stickerData = null, // Full sticker data for modal
  onLoadComplete = null // Callback when card is fully loaded and rendered
}) => {
  // Randomly choose cut corner position (top-left or top-right)
  const cutCornerPosition = React.useMemo(() => {
    return Math.random() < 0.5 ? 'top-left' : 'top-right';
  }, []);

  // Mouse tracking removed - no shimmer animation
  const cardRef = useRef(null);

  // Define clip-path based on corner position
  const getClipPath = () => {
    if (cutCornerPosition === 'top-left') {
      // Cut corner in top-left
      return 'polygon(12px 0, 100% 0, 100% 100%, 0 100%, 0 12px)';
    } else {
      // Cut corner in top-right
      return 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)';
    }
  };

  // Handle click to navigate to sticker in catalog
  const handleClick = useCallback(() => {
    if (onStickerClick) {
      onStickerClick({ 
        id, // pass id if available
        title, 
        size, 
        colorIndex 
      });
    }
  }, [onStickerClick, id, title, size, colorIndex]);

  return (
    <div 
      ref={cardRef}
      className={`prepared-card ${isFlipped ? 'flipped' : ''} ${className}`}
      style={{
        ...(width && { width: `${width}px` }),
        ...(height && { height: `${height}px` }),
        cursor: onStickerClick ? 'pointer' : 'default',
        ...style
      }}
      onClick={handleClick}
    >
      {/* Off-white backing paper */}
      <div 
        className="backing-paper"
        style={{
          clipPath: getClipPath()
        }}
      ></div>
      
      {/* SVG sticker */}
      {svgPath ? (
        <div 
          className="sticker-container"
        >
          <img
            src={svgPath}
            alt={title}
            className="sticker-svg"
            style={{
              filter: getColorFilter(colorIndex)
            }}
            onLoad={() => {
              // Notify parent that card is fully loaded
              if (onLoadComplete) {
                onLoadComplete();
              }
            }}
            onError={(e) => {
              // Only hide the image if it fails to load
              e.target.style.display = 'none';
            }}
          />
        </div>
      ) : null}
      
      {/* Transfer tape with grid */}
      <div className="transfer-tape">
        <div className="tape-grid"></div>
      </div>
      
      {/* Shiny overlay on transfer tape */}
      <div 
        className="transfer-tape-shine"
        style={{
          clipPath: getClipPath()
        }}
      ></div>
      
      {/* Interaction Buttons - Only show on hover when fully generated */}
      {showInteractionButtons && (
        <div className="interaction-buttons">
          <button
            className="interaction-button view-button"
            onClick={(e) => {
              e.stopPropagation();
              if (onStickerClick) {
                // Pass the full sticker data if available, otherwise fall back to basic props
                const clickData = stickerData ? {
                  ...stickerData,
                  size,
                  colorIndex
                } : { id, title, size, colorIndex };
                onStickerClick(clickData);
              }
            }}
            title="View Details"
            aria-label="View sticker details"
          >
            <span className="material-icons">visibility</span>
          </button>
          <button
            className="interaction-button new-button"
            onClick={(e) => {
              e.stopPropagation();
              if (onNewSticker) {
                onNewSticker();
              }
            }}
            title="Generate New Sticker"
            aria-label="Generate new sticker for this port"
          >
            <span className="material-icons">recycling</span>
          </button>
        </div>
      )}

    </div>
  );
};

// PropTypes validation
PreparedStickerCard.propTypes = {
  svgPath: PropTypes.string,
  title: PropTypes.string.isRequired,
  size: PropTypes.string,
  colorIndex: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  onStickerClick: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFlipped: PropTypes.bool,
  isLoading: PropTypes.bool,
  // New props for interaction buttons
  showInteractionButtons: PropTypes.bool,
  onNewSticker: PropTypes.func,
  portId: PropTypes.string,
  stickerData: PropTypes.object,
  onLoadComplete: PropTypes.func
};

// Memoize to prevent unnecessary re-renders in scene canvas
// Re-render only when visual props change
export default React.memo(PreparedStickerCard, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.svgPath === nextProps.svgPath &&
    prevProps.size === nextProps.size &&
    prevProps.colorIndex === nextProps.colorIndex &&
    prevProps.isFlipped === nextProps.isFlipped &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.showInteractionButtons === nextProps.showInteractionButtons
  );
});